import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { signaturesApi, documentsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { BrandLoader } from '../components/BrandLoader';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  FileText, 
  Download, 
  CheckCircle2, 
  Clock, 
  PenTool,
  User,
  Building,
  Calendar,
  AlertCircle,
  Loader2,
  Eye,
  FileCheck,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { 
  getStatusColor, 
  formatDate, 
  formatDateTime,
  getRoleLabel,
  isImageFilename
} from '../lib/utils';

const SIGNATURE_ROLE_LABELS = {
  'EXEC_DIRECTOR': 'Executive Director',
  'DIRECTOR': 'Director',
  'COMPANY_REP': 'Company Representative',
  'PI': 'Principal Investigator',
  'CO_PI': 'Co-Principal Investigator'
};

export default function SigningPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  // iter142 — optional ?doc=<id> URL filter scopes the page to a single doc card
  // (used by the per-doc "Go to Signing Page" button in SignaturesTab).
  const focusDocId = searchParams.get('doc');
  
  const [loading, setLoading] = useState(true);
  const [signingData, setSigningData] = useState(null);
  const [typedName, setTypedName] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const [selectedDocumentsForBatch, setSelectedDocumentsForBatch] = useState([]);
  const [expandedCards, setExpandedCards] = useState({});
  const [previewDocument, setPreviewDocument] = useState(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [signingMode, setSigningMode] = useState('single'); // 'single' or 'batch'
  
  useEffect(() => {
    loadSigningData();
  }, [id]);
  
  const loadSigningData = async () => {
    try {
      setLoading(true);
      const response = await signaturesApi.getSigningPageData(id);
      setSigningData(response.data);
      
      // Auto-expand cards that user can sign
      const expandState = {};
      response.data.document_cards?.forEach(card => {
        if (card.can_sign) {
          expandState[card.document_id] = true;
        }
      });
      setExpandedCards(expandState);
      
      // Pre-select all signable documents for batch signing
      const signableDocs = response.data.document_cards?.filter(c => c.can_sign).map(c => c.document_id) || [];
      setSelectedDocumentsForBatch(signableDocs);
    } catch (error) {
      console.error('Failed to load signing data:', error);
      toast.error('Failed to load signing page');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDownloadDocument = async (docId, filename) => {
    try {
      const response = await documentsApi.download(docId);
      let url = response.data.download_url;
      if (url && url.startsWith('/api/')) {
        url = `${process.env.REACT_APP_BACKEND_URL}${url}`;
      }
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || 'download');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Downloading ${filename}`);
    } catch (error) {
      console.error('Failed to download document:', error);
      toast.error('Failed to download document');
    }
  };

  const handlePreviewDocument = async (doc) => {
    try {
      // Get preview URL with authentication
      const response = await documentsApi.getPreviewUrl(doc.document_id);
      let previewUrl = response.data.preview_url;
      if (previewUrl && previewUrl.startsWith('/api/')) {
        previewUrl = `${process.env.REACT_APP_BACKEND_URL}${previewUrl}`;
      }
      console.log('Preview URL:', previewUrl); // Debug log
      setPreviewDocument({ ...doc, previewUrl });
      setShowPreviewDialog(true);
    } catch (error) {
      console.error('Failed to get preview URL:', error);
      toast.error(error.response?.data?.detail || 'Failed to load document preview');
    }
  };
  
  const handleSignClick = (documentId = null) => {
    if (!typedName.trim()) {
      toast.error('Please type your name to sign');
      return;
    }
    if (!confirmed) {
      toast.error('Please confirm you have reviewed the document(s)');
      return;
    }
    setSelectedDocumentId(documentId);
    setSigningMode(documentId ? 'single' : 'batch');
    setShowConfirmDialog(true);
  };
  
  const handleConfirmSign = async () => {
    try {
      setSubmitting(true);
      
      let response;
      if (signingMode === 'batch' && selectedDocumentsForBatch.length > 0) {
        // Sign multiple documents
        response = await signaturesApi.signMultipleDocuments(id, selectedDocumentsForBatch, typedName.trim());
        
        if (response.data.errors?.length > 0) {
          response.data.errors.forEach(err => toast.error(err));
        }
        if (response.data.signed_documents?.length > 0) {
          toast.success(response.data.message);
        }
      } else {
        // Sign single document
        response = await signaturesApi.submitSignature(id, typedName.trim(), selectedDocumentId);
        toast.success(response.data.message);
      }
      
      if (response.data.all_signed) {
        navigate(`/requests/${id}?tab=signatures`);
      } else {
        await loadSigningData();
        setTypedName('');
        setConfirmed(false);
      }
    } catch (error) {
      console.error('Failed to submit signature:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit signature');
    } finally {
      setSubmitting(false);
      setShowConfirmDialog(false);
      setSelectedDocumentId(null);
    }
  };
  
  const toggleCardExpanded = (docId) => {
    setExpandedCards(prev => ({
      ...prev,
      [docId]: !prev[docId]
    }));
  };

  const toggleDocumentForBatch = (docId) => {
    setSelectedDocumentsForBatch(prev => 
      prev.includes(docId) 
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };
  
  const getSignatureRoleLabel = (role) => SIGNATURE_ROLE_LABELS[role] || role;
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <BrandLoader size="lg" label="Preparing your document…" />
      </div>
    );
  }
  
  if (!signingData) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-slate-600">Unable to load signing page. Please try again.</p>
            <Button onClick={() => navigate(-1)} className="mt-4">
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  const { 
    request_title, 
    request_ref_no, 
    request_type, 
    request_description,
    company_name,
    specialties,
    created_at,
    document_cards = [],
    can_sign,
    can_sign_documents = [],
    user_signature_role,
    deadline
  } = signingData;

  // iter142 — when ?doc=<id> is present, scope the page to just that one doc card
  // so the per-doc "Go to Signing Page" button on a completed/pending doc shows only
  // its own signatories instead of the full request roster.
  const filtered_document_cards = focusDocId
    ? document_cards.filter(d => d.document_id === focusDocId || d.signed_pdf_id === focusDocId)
    : document_cards;
  const signableDocuments = filtered_document_cards.filter(d => d.can_sign);
  const hasMultipleSignable = signableDocuments.length > 1;
  
  return (
    <div className="min-h-screen bg-slate-50" data-testid="signing-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/requests/${id}`)}
                className="text-slate-600"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Request
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <PenTool className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-slate-900">E-Signature Portal</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {/* Request Summary */}
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-start justify-between">
              <div>
                <Badge className={getStatusColor(request_type)} variant="outline">
                  {request_type}
                </Badge>
                <CardTitle className="mt-2 text-2xl">{request_title}</CardTitle>
                <CardDescription className="mt-1">
                  Reference: {request_ref_no}
                </CardDescription>
              </div>
              {deadline && (
                <div className="text-right">
                  <p className="text-sm text-slate-500">Signature Deadline</p>
                  <p className="font-medium text-slate-900">{formatDate(deadline)}</p>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              {company_name && (
                <div className="flex items-center gap-2">
                  <Building className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600">{company_name}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                <span className="text-slate-600">{formatDate(created_at)}</span>
              </div>
              {specialties?.length > 0 && (
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600">{specialties.join(', ')}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Sign All Documents Option */}
        {can_sign && hasMultipleSignable && (
          <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FileCheck className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Sign All Documents at Once</p>
                    <p className="text-sm text-slate-600">
                      You have {signableDocuments.length} documents ready for your signature
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => handleSignClick(null)}
                  disabled={!typedName.trim() || !confirmed || selectedDocumentsForBatch.length === 0}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <PenTool className="w-4 h-4 mr-2" />
                  Sign All ({selectedDocumentsForBatch.length})
                </Button>
              </div>
              
              {/* Signature input for batch signing */}
              <div className="mt-4 pt-4 border-t border-blue-200">
                <div className="grid gap-4">
                  <div>
                    <Label className="text-slate-700">
                      Type your full name to sign <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      value={typedName}
                      onChange={(e) => setTypedName(e.target.value)}
                      placeholder="Enter your full name"
                      className="mt-1 text-lg font-serif bg-white"
                      data-testid="batch-signature-input"
                    />
                  </div>
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="confirm-batch"
                      checked={confirmed}
                      onCheckedChange={setConfirmed}
                    />
                    <Label htmlFor="confirm-batch" className="text-sm text-slate-600 cursor-pointer">
                      I have reviewed all selected documents and confirm the information is accurate. 
                      I understand this electronic signature is legally binding.
                    </Label>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Document Cards */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Documents for Signature ({filtered_document_cards.length})
          </h2>
          
          {filtered_document_cards.length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center py-12">
                <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500">No documents configured for e-signature.</p>
              </CardContent>
            </Card>
          ) : (
            filtered_document_cards.map((doc) => (
              <Card 
                key={doc.document_id}
                className={`transition-all ${
                  doc.all_signed 
                    ? 'border-green-200 bg-green-50/30' 
                    : doc.can_sign 
                      ? 'border-blue-200 bg-blue-50/30' 
                      : 'border-slate-200'
                }`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      {/* Checkbox for batch selection */}
                      {doc.can_sign && hasMultipleSignable && (
                        <Checkbox
                          checked={selectedDocumentsForBatch.includes(doc.document_id)}
                          onCheckedChange={() => toggleDocumentForBatch(doc.document_id)}
                          className="mt-1"
                        />
                      )}
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {doc.all_signed ? (
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                          ) : doc.can_sign ? (
                            <PenTool className="w-5 h-5 text-blue-600" />
                          ) : (
                            <Clock className="w-5 h-5 text-amber-500" />
                          )}
                          <CardTitle className="text-lg">{doc.filename}</CardTitle>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">{doc.doc_type}</Badge>
                          <Badge variant="secondary" className="text-xs">v{doc.version}</Badge>
                          {doc.all_signed && (
                            <Badge className="bg-green-100 text-green-800 text-xs">Fully Signed</Badge>
                          )}
                          {doc.can_sign && !doc.all_signed && (
                            <Badge className="bg-blue-100 text-blue-800 text-xs">Your Turn</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePreviewDocument(doc)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Preview
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadDocument(doc.document_id, doc.filename)}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Download
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleCardExpanded(doc.document_id)}
                      >
                        {expandedCards[doc.document_id] ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                
                {expandedCards[doc.document_id] && (
                  <CardContent className="pt-0">
                    {/* Signatures for this document */}
                    <div className="border-t pt-4">
                      <p className="text-sm font-medium text-slate-700 mb-3">Signatures Required:</p>
                      <div className="space-y-2">
                        {doc.signatures?.map((sig, idx) => (
                          <div 
                            key={sig.id}
                            className={`flex items-center justify-between p-3 rounded-lg border ${
                              sig.status === 'SIGNED' 
                                ? 'bg-green-50 border-green-200' 
                                : sig.is_current_user && doc.can_sign
                                  ? 'bg-blue-50 border-blue-300'
                                  : 'bg-slate-50 border-slate-200'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-medium text-slate-500 w-6">{idx + 1}.</span>
                              {sig.status === 'SIGNED' ? (
                                <CheckCircle2 className="w-4 h-4 text-green-600" />
                              ) : (
                                <Clock className="w-4 h-4 text-amber-500" />
                              )}
                              <div>
                                <p className="font-medium text-slate-900">{sig.user_name}</p>
                                <p className="text-xs text-slate-500">{getSignatureRoleLabel(sig.signature_role)}</p>
                              </div>
                            </div>
                            <div>
                              {sig.status === 'SIGNED' ? (
                                <div className="text-right">
                                  <p className="text-sm font-serif italic text-green-700">{sig.typed_name}</p>
                                  <p className="text-xs text-slate-500">{formatDateTime(sig.signed_at)}</p>
                                </div>
                              ) : sig.is_current_user && doc.can_sign ? (
                                <Badge className="bg-blue-600">Your Turn</Badge>
                              ) : (
                                <Badge variant="outline" className="text-slate-500">Pending</Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Individual Sign Button */}
                    {doc.can_sign && !doc.all_signed && (
                      <div className="mt-4 pt-4 border-t">
                        <div className="bg-white rounded-lg p-4 border">
                          <p className="text-sm font-medium text-slate-700 mb-3">
                            Sign this document as {getSignatureRoleLabel(user_signature_role)}
                          </p>
                          <div className="space-y-3">
                            <div>
                              <Input
                                value={typedName}
                                onChange={(e) => setTypedName(e.target.value)}
                                placeholder="Type your full name"
                                className="font-serif"
                                data-testid={`signature-input-${doc.document_id}`}
                              />
                            </div>
                            <div className="flex items-start gap-2">
                              <Checkbox
                                id={`confirm-${doc.document_id}`}
                                checked={confirmed}
                                onCheckedChange={setConfirmed}
                              />
                              <Label htmlFor={`confirm-${doc.document_id}`} className="text-xs text-slate-600 cursor-pointer">
                                I have reviewed this document and confirm the information is accurate.
                              </Label>
                            </div>
                            <Button
                              onClick={() => handleSignClick(doc.document_id)}
                              disabled={!typedName.trim() || !confirmed || submitting}
                              className="w-full bg-blue-600 hover:bg-blue-700"
                              data-testid={`sign-btn-${doc.document_id}`}
                            >
                              <PenTool className="w-4 h-4 mr-2" />
                              Sign "{doc.filename}"
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            ))
          )}
        </div>
        
        {/* Read-only notice for users who can't sign yet */}
        {!can_sign && user_signature_role && (
          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <Clock className="w-6 h-6 text-amber-600 mt-1" />
                <div>
                  <p className="font-medium text-amber-900">Waiting for Previous Signatures</p>
                  <p className="text-sm text-amber-700 mt-1">
                    You will be notified when it's your turn to sign. Please review the documents 
                    in the meantime.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      
      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Your Signature</DialogTitle>
            <DialogDescription>
              {signingMode === 'batch' 
                ? `You are about to sign ${selectedDocumentsForBatch.length} document(s):`
                : 'You are about to sign this document:'
              }
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {signingMode === 'batch' ? (
              <div className="space-y-2 mb-4 max-h-32 overflow-y-auto">
                {document_cards
                  .filter(d => selectedDocumentsForBatch.includes(d.document_id))
                  .map(d => (
                    <div key={d.document_id} className="flex items-center gap-2 text-sm p-2 bg-slate-50 rounded">
                      <FileText className="w-4 h-4 text-blue-600" />
                      <span>{d.filename}</span>
                      <Badge variant="outline" className="text-xs">{d.doc_type}</Badge>
                    </div>
                  ))
                }
              </div>
            ) : (
              <div className="mb-4 p-2 bg-slate-50 rounded flex items-center gap-2">
                <FileText className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium">
                  {document_cards.find(d => d.document_id === selectedDocumentId)?.filename}
                </span>
              </div>
            )}
            
            <div className="p-4 bg-slate-50 rounded-lg border text-center">
              <p className="text-2xl font-serif italic text-blue-900">{typedName}</p>
              <p className="text-sm text-slate-500 mt-2">
                {getSignatureRoleLabel(user_signature_role)} • {formatDateTime(new Date().toISOString())}
              </p>
            </div>
            <p className="text-sm text-slate-600 mt-4">
              This action cannot be undone. Are you sure you want to proceed?
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmSign}
              disabled={submitting}
              className="bg-blue-900 hover:bg-blue-950"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing...
                </>
              ) : (
                'Confirm & Sign'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {previewDocument?.filename}
            </DialogTitle>
            <DialogDescription>
              {previewDocument?.doc_type} • Version {previewDocument?.version}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {previewDocument && (
              <div className="border rounded-lg overflow-hidden bg-slate-100">
                {isImageFilename(previewDocument.filename) && previewDocument.previewUrl ? (
                  // Image preview — raw image, no A4 wrap (the wrap happens only when signing).
                  <div className="w-full h-[60vh] flex items-center justify-center bg-slate-50">
                    <img
                      src={previewDocument.previewUrl}
                      alt={previewDocument.filename}
                      className="max-w-full max-h-full object-contain"
                      data-testid="document-preview-image"
                    />
                  </div>
                ) : previewDocument.filename?.toLowerCase().endsWith('.pdf') && previewDocument.previewUrl ? (
                  <object
                    data={previewDocument.previewUrl}
                    type="application/pdf"
                    className="w-full h-[60vh]"
                    title={previewDocument.filename}
                  >
                    <div className="p-8 text-center">
                      <FileText className="w-16 h-16 mx-auto mb-4 text-slate-400" />
                      <p className="text-slate-600 mb-4">
                        PDF preview is not supported in your browser.
                      </p>
                      <Button
                        onClick={() => handleDownloadDocument(previewDocument.document_id, previewDocument.filename)}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download to View
                      </Button>
                    </div>
                  </object>
                ) : (
                  <div className="p-8 text-center">
                    <FileText className="w-16 h-16 mx-auto mb-4 text-slate-400" />
                    <p className="text-slate-600 mb-4">
                      {previewDocument.filename?.toLowerCase().endsWith('.pdf') 
                        ? 'Loading preview...' 
                        : 'Preview not available for this file type.'
                      }
                    </p>
                    <Button
                      onClick={() => handleDownloadDocument(previewDocument.document_id, previewDocument.filename)}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download to View
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
              Close
            </Button>
            {previewDocument && (
              <Button onClick={() => handleDownloadDocument(previewDocument.document_id, previewDocument.filename)}>
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
